import { MenuOutlined, SelectOutlined } from '@ant-design/icons'
import { useVirtualizer } from '@tanstack/react-virtual'
import { useDebounce } from '@uidotdev/usehooks'
import { Modal } from 'antd'
import { AxiosError } from 'axios'
import {
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { toast } from 'sonner'
import { ZodError } from 'zod'

import { DEFAULT_PAGE_SIZE, DELAY, GENERAL_ERRORS_MESSAGES } from '@/constants'
import { ScreenSizeContext } from '@/providers/ScreenSizeProvider'
import { useUserInfoStore } from '@/stores/user/userInfoStore'
import type { OrganisationInterface } from '@/types'
import api from '@/utils/api/api'

import ItemsWithLoading from '../../Atoms/ItemsWithLoading/ItemsWithLoading'
import OrganisationListItemContent from '../OrganisationListItemContent/OrganisationListItemContent'
import { getIsVatNumber } from '../OrganisationsSearch.config'
import generalCn from '../OrganisationsSearch.module.scss'

type AllOrganisationsPropsType = {
  handleChooseOrganisation: (organisation: OrganisationInterface) => void
  controlled?: boolean
  open?: boolean
  onClose?: () => void
  hideButton?: boolean
  trigger?: { className: string; icon: ReactNode; label: string }
}

function AllOrganisations(props: AllOrganisationsPropsType) {
  const {
    handleChooseOrganisation,
    controlled = false,
    open,
    onClose,
    hideButton = false,
    trigger,
  } = props

  const [allOrganisations, setAllOrganisations] = useState<
    OrganisationInterface[]
  >([])
  const [openAllOrganisationsModal, setOpenAllOrganisationsModal] =
    useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [allOrganisationsSearch, setAllOrganisationsSearch] =
    useState<string>('')
  const [error, setError] = useState<string>('')

  const pageRef = useRef(0)
  const hasMoreRef = useRef(true)
  const isLoadingRef = useRef(false)

  const screen = useContext(ScreenSizeContext)

  const [modal] = Modal.useModal()

  const debouncedAllOrganisationsSearch = useDebounce(
    allOrganisationsSearch,
    DELAY
  )

  const { selectedOrganisation } = useUserInfoStore((state) => state)

  const parentRef = useRef<HTMLDivElement>(null)

  const internalOpen = controlled ? open : openAllOrganisationsModal
  const setInternalOpen = controlled
    ? () => onClose?.()
    : () => setOpenAllOrganisationsModal(false)

  const estimateOrgRowSize = useCallback(
    () => (screen?.xs ? 84 : 70),
    [screen?.xs]
  )

  const rowVirtualizer = useVirtualizer({
    count: allOrganisations.length,
    getScrollElement: () => parentRef.current,
    estimateSize: estimateOrgRowSize,
    overscan: 10,
  })

  const virtualItems = rowVirtualizer.getVirtualItems()
  const lastItemIndex =
    virtualItems.length > 0 ? virtualItems[virtualItems.length - 1].index : -1

  const fetchOrganisations = useCallback(
    async (pageNumber: number) => {
      if (pageNumber < pageRef.current) return
      if (isLoadingRef.current || !hasMoreRef.current) {
        return
      }

      isLoadingRef.current = true
      setLoading(true)
      setError('')

      const value = debouncedAllOrganisationsSearch

      try {
        const response = await api.get(`/organisations`, {
          params: {
            ...(getIsVatNumber(value) && { vatNumber: value }),
            ...(!getIsVatNumber(value) && { description: value }),
            pageSize: DEFAULT_PAGE_SIZE * 4,
            page: pageNumber,
          },
        })

        const orgData = response.data.organisations
        const content = orgData?.content || []
        const totalPages = orgData?.totalPages || 0
        const last = pageNumber >= totalPages - 1

        if (content.length === 0) {
          hasMoreRef.current = false
          if (pageNumber === 0) {
            toast.info('Организации не найдены', {
              description: 'Попробуйте изменить поисковый запрос',
            })
          }
        } else {
          setAllOrganisations((prev) => {
            const existingIds = new Set(prev.map((org) => org.id))
            const newOrgs = content.filter(
              (org: OrganisationInterface) => !existingIds.has(org.id)
            )
            return [...prev, ...newOrgs]
          })
          hasMoreRef.current = !last
          pageRef.current = pageNumber + 1
        }
      } catch (error) {
        if (error instanceof AxiosError) {
          const serverMessage =
            error.response?.data?.message ||
            'Произошла ошибка при загрузке данных'
          setError(serverMessage)
        }
        // if (axios.isAxiosError(error)) {
        //   handleApiError(error, modal)
        // }
        if (error instanceof ZodError) {
          setError(GENERAL_ERRORS_MESSAGES.ZOD_ERROR)
          toast.error(GENERAL_ERRORS_MESSAGES.ZOD_ERROR)
        }
        hasMoreRef.current = false
      } finally {
        isLoadingRef.current = false
        setLoading(false)
      }
    },
    [debouncedAllOrganisationsSearch, modal]
  )

  useEffect(() => {
    if (!internalOpen || lastItemIndex === -1) return

    if (
      lastItemIndex >= allOrganisations.length - 1 &&
      hasMoreRef.current &&
      !isLoadingRef.current
    ) {
      fetchOrganisations(pageRef.current)
    }
  }, [
    internalOpen,
    allOrganisations.length,
    lastItemIndex,
    fetchOrganisations,
    hasMoreRef,
    isLoadingRef,
  ])

  useEffect(() => {
    setAllOrganisations([])
    pageRef.current = 0
    hasMoreRef.current = true
    isLoadingRef.current = false

    if (internalOpen) {
      fetchOrganisations(0)
    }
  }, [debouncedAllOrganisationsSearch, internalOpen, fetchOrganisations])

  const resetAndShowModal = () => {
    setAllOrganisations([])
    setAllOrganisationsSearch('')
    pageRef.current = 0
    hasMoreRef.current = true
    isLoadingRef.current = false
    setError('')
    setOpenAllOrganisationsModal(true)
  }

  const handleModalOpenClick = () => {
    if (Object.keys(selectedOrganisation).length) {
      toast.warning('Отмените текущего клиента', {
        description:
          'Пожалуйста, отмените выбранную организацию перед выбором новой',
      })
    } else {
      resetAndShowModal()
    }
  }

  const handleChooseOrg = (organisation: OrganisationInterface) => {
    handleChooseOrganisation(organisation)
    setInternalOpen()
  }

  const handleCancelModal = () => {
    setInternalOpen()
    if (parentRef.current) {
      parentRef.current.scrollTop = 0
    }
  }

  return (
    <>
      {!controlled && !hideButton && trigger && (
        <button
          type="button"
          className={trigger.className}
          aria-label={trigger.label}
          title={trigger.label}
          onClick={handleModalOpenClick}
        >
          {trigger.icon}
        </button>
      )}
      {!controlled && !hideButton && !trigger && (
        <button
          className={
            Object.keys(selectedOrganisation).length
              ? generalCn.button
              : [generalCn.button, generalCn.disabled].join(' ')
          }
          onClick={handleModalOpenClick}
        >
          <div>
            {[screen?.xs, screen?.sm].some((value) => value) ? (
              <SelectOutlined />
            ) : (
              <MenuOutlined />
            )}
          </div>
        </button>
      )}

      <Modal
        open={internalOpen}
        className={generalCn.modal}
        title="Выбор организации"
        centered={true}
        onCancel={handleCancelModal}
        footer={null}
        destroyOnHidden
      >
        <div className={generalCn.organisationsSearchBar}>
          <input
            value={allOrganisationsSearch}
            onChange={(e) => setAllOrganisationsSearch(e.target.value)}
            placeholder="Введите название или УНП организации..."
            autoFocus
          />
        </div>
        <ItemsWithLoading
          loading={loading && allOrganisations.length === 0}
          items={allOrganisations}
          empty={
            <p className={generalCn.organisationsSearchEmptyMessage}>
              Ничего не найдено
            </p>
          }
          loaderPaddingTop={216}
          loadingError={error}
          error={
            <p className={generalCn.organisationsSearchEmptyMessage}>{error}</p>
          }
        >
          <div
            ref={parentRef}
            className={`${generalCn.organisationsScroll} ${screen?.xs ? generalCn.organisationsScrollCompact : ''}`}
          >
            <div
              className={generalCn.organisationsVirtualList}
              style={{
                '--virtual-total-size': `${rowVirtualizer.getTotalSize()}px`,
              }}
            >
              {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                const organisation = allOrganisations[virtualRow.index]

                return (
                  <div
                    key={organisation.id}
                    style={{
                      '--virtual-row-size': `${virtualRow.size}px`,
                      '--virtual-row-start': `${virtualRow.start}px`,
                    }}
                    className={`${generalCn.listItem} ${generalCn.organisationsVirtualRow}`}
                    onClick={() => handleChooseOrg(organisation)}
                  >
                    <OrganisationListItemContent
                      organisation={organisation}
                      withEmail={!screen?.xs}
                    />
                  </div>
                )
              })}
            </div>
          </div>
        </ItemsWithLoading>
      </Modal>
    </>
  )
}

export default AllOrganisations
